import { useSearchParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { supabase } from "../../lib/supabase";
import toast from "react-hot-toast";
import { useSchoolYear } from "../../context/SchoolYearContext";
import { useAuth } from "../../context/AuthContext";
import {
  calculateExpenseAmounts,
  formatMoneyInput,
} from "../../lib/finance";
import {
  COLLECTION_CREATE_RESULT,
  createCollection,
} from "../../lib/collections/createCollection";
import {
  COLLECTION_CONTRIBUTION_MODE,
  validateCollectionContributionConfig,
} from "../../lib/collections/contributionConfig";
import {
  buildPlannedExpenseMutation,
  parseOptionalPlannedAmount,
} from "../../lib/plannedExpenses";

const TABS = ["Wydatek", "Zbiórka", "Planowany wydatek", "Przypomnienie", "Użytkownik"];

function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <label className="mb-2 block text-xs font-medium uppercase tracking-[0.14em] text-slate-400">
        {label}
      </label>
      {children}
    </div>
  );
}

function inputClass() {
  return "w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition-colors focus:border-emerald-300";
}

function textareaClass() {
  return "w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition-colors focus:border-emerald-300 resize-y";
}

function primaryButtonClass(disabled = false) {
  return `inline-flex items-center justify-center rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-500 px-5 py-3 text-sm font-semibold text-white shadow-[0_10px_24px_rgba(16,185,129,0.22)] transition-transform duration-200 ${
    disabled ? "opacity-60 cursor-not-allowed" : "hover:-translate-y-0.5"
  }`;
}

function ReadOnlyYearMessage() {
  return (
    <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-medium text-amber-800">
      Rok tylko do odczytu.
    </div>
  );
}

function DraftYearMessage() {
  return (
    <div className="rounded-2xl border border-sky-200 bg-sky-50 px-4 py-3 text-sm font-medium text-sky-800">
      Wersja robocza roku szkolnego. Dane mogą edytować wyłącznie administratorzy.
    </div>
  );
}

function AddExpenseForm() {
  const { isAdmin } = useAuth();
  const {
    selectedSchoolYear,
    selectedSchoolYearId,
    isSelectedYearActive,
    isSelectedYearDraft,
  } = useSchoolYear();
  const canWrite = isAdmin && (isSelectedYearActive || isSelectedYearDraft);
  const [form, setForm] = useState({
    occasion: "",
    purchase_date: "",
    item_or_service: "",
    quantity: "",
    unit_price: "",
    delivery_cost: "",
    total_value: "",
    confirmation_type: "",
    cost_per_child: "",
    payout_date: "",
    payout_amount: "",
    student_count_snapshot: "",
    status: "Zakupione / Nie wypłacone",
    payment_source: "",
    receipt_link: "",
    notes: "",
  });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    setForm((current) => ({
      ...current,
      student_count_snapshot: selectedSchoolYear?.student_count ?? "",
    }));
  }, [selectedSchoolYearId, selectedSchoolYear?.student_count]);

  const expenseAmounts = calculateExpenseAmounts({
    quantity: form.quantity,
    unitPrice: form.unit_price,
    deliveryCost: form.delivery_cost,
    totalValue: form.total_value,
    studentCount: form.student_count_snapshot,
  });

  function set(k, v) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!canWrite || !selectedSchoolYearId) {
      toast.error("Rok tylko do odczytu.");
      return;
    }
    if (!expenseAmounts.hasStudentCount) {
      toast.error("Podaj poprawną liczbę dzieci objętych wydatkiem.");
      return;
    }
    if (expenseAmounts.totalValue === null || expenseAmounts.totalValue <= 0) {
      toast.error("Podaj poprawną łączną wartość wydatku.");
      return;
    }
    setSaving(true);

    const payload = {
      ...form,
      school_year_id: selectedSchoolYearId,
      quantity: form.quantity === "" ? null : Number(form.quantity),
      unit_price: form.unit_price === "" ? null : Number(form.unit_price),
      delivery_cost: form.delivery_cost === "" ? null : Number(form.delivery_cost),
      total_value: expenseAmounts.totalValue,
      cost_per_child: expenseAmounts.costPerChild,
      payout_amount: expenseAmounts.payoutAmount,
      student_count_snapshot: expenseAmounts.studentCountSnapshot,
    };

    const { error } = await supabase.from("expenses").insert([payload]);

    setSaving(false);

    if (error) {
      toast.error("Błąd: " + error.message);
    } else {
      toast.success("Wydatek dodany!");
      setForm({
        ...form,
        item_or_service: "",
        receipt_link: "",
        notes: "",
        student_count_snapshot: selectedSchoolYear?.student_count ?? "",
      });
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Okazja *">
          <input
            className={inputClass()}
            required
            value={form.occasion}
            onChange={(e) => set("occasion", e.target.value)}
            placeholder="np. Dzień nauczyciela"
          />
        </Field>

        <Field label="Data zakupu">
          <input
            className={inputClass()}
            type="date"
            value={form.purchase_date}
            onChange={(e) => set("purchase_date", e.target.value)}
          />
        </Field>

        <Field label="Towar / Usługa *" className="sm:col-span-2">
          <input
            className={inputClass()}
            required
            value={form.item_or_service}
            onChange={(e) => set("item_or_service", e.target.value)}
            placeholder="np. Kwiaty dla pani wychowawcy"
          />
        </Field>

        <Field label="Ilość">
          <input
            className={inputClass()}
            type="number"
            value={form.quantity}
            onChange={(e) => set("quantity", e.target.value)}
          />
        </Field>

        <Field label="Cena szt. (PLN)">
          <input
            className={inputClass()}
            type="number"
            step="0.01"
            value={form.unit_price}
            onChange={(e) => set("unit_price", e.target.value)}
          />
        </Field>

        <Field label="Koszt dostawy (PLN)">
          <input
            className={inputClass()}
            type="number"
            step="0.01"
            value={form.delivery_cost}
            onChange={(e) => set("delivery_cost", e.target.value)}
          />
        </Field>

        <Field label="Łączna wartość (PLN) *">
          <input
            className={inputClass()}
            type="number"
            step="0.01"
            required
            value={
              expenseAmounts.hasItemizedValues
                ? formatMoneyInput(expenseAmounts.totalValue)
                : form.total_value
            }
            onChange={(e) => set("total_value", e.target.value)}
            readOnly={expenseAmounts.hasItemizedValues}
          />
          {expenseAmounts.hasItemizedValues && (
            <p className="mt-2 text-xs text-slate-500">
              Wyliczono automatycznie: ilość × cena szt. + dostawa.
            </p>
          )}
        </Field>

        <Field label="Potwierdzenie">
          <input
            className={inputClass()}
            value={form.confirmation_type}
            onChange={(e) => set("confirmation_type", e.target.value)}
            placeholder="np. Paragon, Faktura"
          />
        </Field>

        <Field label="Liczba dzieci objętych wydatkiem">
          <input
            className={inputClass()}
            type="number"
            min="1"
            step="1"
            required
            value={form.student_count_snapshot}
            onChange={(e) => set("student_count_snapshot", e.target.value)}
          />
          <p className="mt-2 text-xs text-slate-500">
            Domyślnie liczba dzieci z wybranego roku szkolnego; możesz ją zmienić dla tego wydatku.
          </p>
        </Field>

        <Field label="Koszt/dziecko (PLN)">
          <input
            className={inputClass()}
            type="number"
            step="0.01"
            value={formatMoneyInput(expenseAmounts.costPerChild)}
            readOnly
          />
          {!expenseAmounts.hasStudentCount && (
            <p className="mt-2 text-xs font-medium text-amber-700">
              Podaj dodatnią liczbę całkowitą dzieci objętych wydatkiem.
            </p>
          )}
        </Field>

        <Field label="Status">
          <select
            className={inputClass()}
            value={form.status}
            onChange={(e) => set("status", e.target.value)}
          >
            <option>Zakupione / Nie wypłacone</option>
            <option>Wypłacone</option>
            <option>Planowane</option>
            <option>Anulowane</option>
          </select>
        </Field>

        <Field label="Data wypłaty">
          <input
            className={inputClass()}
            type="date"
            value={form.payout_date}
            onChange={(e) => set("payout_date", e.target.value)}
          />
        </Field>

        <Field label="Kwota wypłaty (PLN)">
          <input
            className={inputClass()}
            type="number"
            step="0.01"
            value={formatMoneyInput(expenseAmounts.payoutAmount)}
            readOnly
          />
        </Field>

        <Field label="Źródło płatności">
          <input
            className={inputClass()}
            value={form.payment_source}
            onChange={(e) => set("payment_source", e.target.value)}
            placeholder="np. prywatne, zbiórka"
          />
        </Field>

        <Field label="Link do paragonu" className="sm:col-span-2">
          <input
            className={inputClass()}
            type="url"
            value={form.receipt_link}
            onChange={(e) => set("receipt_link", e.target.value)}
            placeholder="https://..."
          />
        </Field>

        <Field label="Notatki" className="sm:col-span-2">
          <textarea
            className={textareaClass()}
            rows={3}
            value={form.notes}
            onChange={(e) => set("notes", e.target.value)}
          />
        </Field>
      </div>

      {isSelectedYearDraft && <DraftYearMessage />}
      {!canWrite && <ReadOnlyYearMessage />}

      <button
        type="submit"
        className={primaryButtonClass(saving || !canWrite)}
        disabled={saving || !canWrite}
      >
        {saving ? "Dodawanie..." : "Dodaj wydatek"}
      </button>
    </form>
  );
}

function AddCollectionForm() {
  const { isAdmin } = useAuth();
  const { selectedSchoolYearId, isSelectedYearActive, isSelectedYearDraft } = useSchoolYear();
  const canWrite = isAdmin && (isSelectedYearActive || isSelectedYearDraft);
  const [form, setForm] = useState({
    name: "",
    description: "",
    target_amount: "",
    current_amount: "",
    status: "aktywna",
    contribution_mode: COLLECTION_CONTRIBUTION_MODE.FIXED,
    is_voluntary: false,
    min_contribution: "",
    max_contribution: "",
    start_date: "",
    end_date: "",
    affects_class_balance: true,
  });
  const [saving, setSaving] = useState(false);

  function set(k, v) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  function setContributionConfig(k, v) {
    set(k, v);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!canWrite || !selectedSchoolYearId) {
      toast.error("Rok tylko do odczytu.");
      return;
    }
    const contributionConfig = validateCollectionContributionConfig({
      contributionMode: form.contribution_mode,
      isVoluntary: form.is_voluntary,
      minContribution: form.min_contribution,
      maxContribution: form.max_contribution,
    });
    if (!contributionConfig.valid) {
      toast.error(
        contributionConfig.error === "MIN_BELOW_ONE_ZLOTY"
          ? "Minimalna wpłata musi wynosić co najmniej 1,00 zł."
          : contributionConfig.error === "MAX_BELOW_MIN"
            ? "Maksymalna wpłata nie może być niższa od minimalnej."
            : "Uzupełnij poprawny przedział wpłat."
      );
      return;
    }
    const targetAmount = form.target_amount === "" ? null : Number(form.target_amount);
    if (
      contributionConfig.contributionMode === COLLECTION_CONTRIBUTION_MODE.FIXED &&
      (!Number.isFinite(targetAmount) || targetAmount <= 0)
    ) {
      toast.error("Cel zbiórki musi być większy od 0.");
      return;
    }
    if (targetAmount !== null && (!Number.isFinite(targetAmount) || targetAmount <= 0)) {
      toast.error("Łączny cel zbiórki musi być większy od 0 albo pozostać pusty.");
      return;
    }
    if (form.start_date && form.end_date && form.end_date < form.start_date) {
      toast.error("Data końcowa nie może być wcześniejsza od daty rozpoczęcia.");
      return;
    }
    try {
      setSaving(true);
      const result = await createCollection({
        name: form.name,
        description: form.description,
        targetAmount,
        currentAmount: parseFloat(form.current_amount) || 0,
        status: form.status,
        schoolYearId: selectedSchoolYearId,
        contributionMode: contributionConfig.contributionMode,
        isVoluntary: contributionConfig.isVoluntary,
        minContribution: contributionConfig.minContribution,
        maxContribution: contributionConfig.maxContribution,
        startDate: form.start_date || null,
        endDate: form.end_date || null,
        affectsClassBalance: form.affects_class_balance,
      });

      if (result === COLLECTION_CREATE_RESULT.ALREADY_EXISTS) {
        toast("Ta zbiórka już istnieje.");
        return;
      }

      toast.success("Zbiórka dodana!");
      setForm({
        name: "",
        description: "",
        target_amount: "",
        current_amount: "",
        status: "aktywna",
        contribution_mode: COLLECTION_CONTRIBUTION_MODE.FIXED,
        is_voluntary: false,
        min_contribution: "",
        max_contribution: "",
        start_date: "",
        end_date: "",
        affects_class_balance: true,
      });
    } catch (error) {
      toast.error("Błąd: " + (error.message || "Nie udało się dodać zbiórki."));
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Nazwa zbiórki *" className="sm:col-span-2">
          <input
            className={inputClass()}
            required
            value={form.name}
            onChange={(e) => set("name", e.target.value)}
          />
        </Field>

        <Field label="Opis" className="sm:col-span-2">
          <textarea
            className={textareaClass()}
            rows={3}
            value={form.description}
            onChange={(e) => set("description", e.target.value)}
          />
        </Field>

        <Field label={`Łączny cel zbiórki (PLN)${form.contribution_mode === COLLECTION_CONTRIBUTION_MODE.FIXED ? " *" : ""}`}>
          <input
            className={inputClass()}
            type="number"
            step="0.01"
            required={form.contribution_mode === COLLECTION_CONTRIBUTION_MODE.FIXED}
            value={form.target_amount}
            onChange={(e) => set("target_amount", e.target.value)}
          />
        </Field>

        <Field label="Sposób wpłaty" className="sm:col-span-2">
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="flex cursor-pointer items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700">
              <input
                type="radio"
                name="contribution-mode"
                checked={form.contribution_mode === COLLECTION_CONTRIBUTION_MODE.FIXED}
                onChange={() => setContributionConfig("contribution_mode", COLLECTION_CONTRIBUTION_MODE.FIXED)}
              />
              Stała kwota
            </label>
            <label className="flex cursor-pointer items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700">
              <input
                type="radio"
                name="contribution-mode"
                checked={form.contribution_mode === COLLECTION_CONTRIBUTION_MODE.FLEXIBLE_RANGE}
                onChange={() => setContributionConfig("contribution_mode", COLLECTION_CONTRIBUTION_MODE.FLEXIBLE_RANGE)}
              />
              Dowolna kwota w przedziale
            </label>
          </div>
        </Field>

        <Field label="Charakter zbiórki" className="sm:col-span-2">
          <label className="flex cursor-pointer items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-700">
            <input
              type="checkbox"
              checked={form.is_voluntary}
              onChange={(e) => setContributionConfig("is_voluntary", e.target.checked)}
            />
            Zbiórka dobrowolna
          </label>
        </Field>

        {form.contribution_mode === COLLECTION_CONTRIBUTION_MODE.FLEXIBLE_RANGE && (
          <>
            <Field label="Minimalna wpłata (PLN) *">
              <input
                className={inputClass()}
                type="number"
                step="0.01"
                min="1"
                required
                value={form.min_contribution}
                onChange={(e) => setContributionConfig("min_contribution", e.target.value)}
              />
            </Field>
            <Field label="Maksymalna wpłata (PLN) *">
              <input
                className={inputClass()}
                type="number"
                step="0.01"
                min="1"
                required
                value={form.max_contribution}
                onChange={(e) => setContributionConfig("max_contribution", e.target.value)}
              />
            </Field>
            <div className="sm:col-span-2 rounded-2xl bg-emerald-50 px-4 py-3 text-sm leading-6 text-emerald-800">
              Każda osoba może wpłacić dowolną kwotę w zapisanym przedziale. Maksymalna wpłata nie jest łącznym celem zbiórki.
            </div>
          </>
        )}

        <Field label="Zebrano (PLN)">
          <input
            className={inputClass()}
            type="number"
            step="0.01"
            value={form.current_amount}
            onChange={(e) => set("current_amount", e.target.value)}
          />
        </Field>

        <Field label="Wpłaty od">
          <input
            className={inputClass()}
            type="date"
            value={form.start_date}
            onChange={(e) => set("start_date", e.target.value)}
          />
        </Field>

        <Field label="Wpłaty do">
          <input
            className={inputClass()}
            type="date"
            min={form.start_date || undefined}
            value={form.end_date}
            onChange={(e) => set("end_date", e.target.value)}
          />
        </Field>

        <Field label="Saldo klasy" className="sm:col-span-2">
          <label className="flex cursor-pointer items-start gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700">
            <input
              className="mt-1"
              type="checkbox"
              checked={form.affects_class_balance}
              onChange={(e) => set("affects_class_balance", e.target.checked)}
            />
            <span>
              <span className="font-medium">Uwzględniaj zebrane środki w saldzie klasy</span>
              <span className="mt-1 block text-xs leading-5 text-slate-500">
                Wyłącz dla zbiórek, których środki są przekazywane dalej i nie należą do budżetu klasy.
              </span>
            </span>
          </label>
        </Field>

        <Field label="Status">
          <select
            className={inputClass()}
            value={form.status}
            onChange={(e) => set("status", e.target.value)}
          >
            <option value="aktywna">Aktywna</option>
            <option value="zakończona">Zakończona</option>
            <option value="anulowana">Anulowana</option>
          </select>
        </Field>
      </div>

      {isSelectedYearDraft && <DraftYearMessage />}
      {!canWrite && <ReadOnlyYearMessage />}

      <button
        type="submit"
        className={primaryButtonClass(saving || !canWrite)}
        disabled={saving || !canWrite}
      >
        {saving ? "Dodawanie..." : "Dodaj zbiórkę"}
      </button>
    </form>
  );
}

function AddPlannedForm() {
  const { isAdmin } = useAuth();
  const { selectedSchoolYearId, isSelectedYearActive, isSelectedYearDraft } = useSchoolYear();
  const canWrite = isAdmin && (isSelectedYearActive || isSelectedYearDraft);
  const [form, setForm] = useState({
    occasion: "",
    planned_date: "",
    amount: "",
    amount_per_child: "",
    description: "",
    status: "Planowane",
  });
  const [saving, setSaving] = useState(false);

  function set(k, v) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!canWrite || !selectedSchoolYearId) {
      toast.error("Rok tylko do odczytu.");
      return;
    }
    if (form.amount && parseOptionalPlannedAmount(form.amount) === null) {
      toast.error("Kwota musi być większa od zera albo pozostać pusta.");
      return;
    }
    setSaving(true);

    const { error } = await supabase.from("planned_expenses").insert([
      {
        ...buildPlannedExpenseMutation(form),
        school_year_id: selectedSchoolYearId,
      },
    ]);

    setSaving(false);

    if (error) {
      toast.error("Błąd: " + error.message);
    } else {
      toast.success("Planowany wydatek dodany!");
      setForm({
        occasion: "",
        planned_date: "",
        amount: "",
        amount_per_child: "",
        description: "",
        status: "Planowane",
      });
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Okazja *">
          <input
            className={inputClass()}
            required
            value={form.occasion}
            onChange={(e) => set("occasion", e.target.value)}
          />
        </Field>

        <Field label="Planowana data">
          <input
            className={inputClass()}
            type="date"
            value={form.planned_date}
            onChange={(e) => set("planned_date", e.target.value)}
          />
        </Field>

        <Field label="Kwota (PLN)">
          <input
            className={inputClass()}
            type="number"
            step="0.01"
            min="0.01"
            value={form.amount}
            onChange={(e) => set("amount", e.target.value)}
          />
          <div className="mt-2 text-xs leading-5 text-slate-500">
            Zostaw puste, jeśli kwota nie jest jeszcze znana.
          </div>
        </Field>

        <Field label="Kwota/dziecko (PLN)">
          <input
            className={inputClass()}
            type="number"
            step="0.01"
            value={form.amount_per_child}
            onChange={(e) => set("amount_per_child", e.target.value)}
          />
        </Field>

        <Field label="Opis" className="sm:col-span-2">
          <textarea
            className={textareaClass()}
            rows={3}
            value={form.description}
            onChange={(e) => set("description", e.target.value)}
          />
        </Field>

        <Field label="Status">
          <select
            className={inputClass()}
            value={form.status}
            onChange={(e) => set("status", e.target.value)}
          >
            <option>Planowane</option>
            <option>Zrealizowane</option>
            <option>Anulowane</option>
          </select>
        </Field>
      </div>

      {isSelectedYearDraft && <DraftYearMessage />}
      {!canWrite && <ReadOnlyYearMessage />}

      <button
        type="submit"
        className={primaryButtonClass(saving || !canWrite)}
        disabled={saving || !canWrite}
      >
        {saving ? "Dodawanie..." : "Dodaj planowany wydatek"}
      </button>
    </form>
  );
}

function AddNoticeForm() {
  const { isAdmin } = useAuth();
  const { selectedSchoolYearId, isSelectedYearActive, isSelectedYearDraft } = useSchoolYear();
  const canWrite = isAdmin && (isSelectedYearActive || isSelectedYearDraft);
  const [form, setForm] = useState({
    title: "",
    description: "",
    amount_per_child: "",
    due_date: "",
    status: "Aktywne",
  });
  const [saving, setSaving] = useState(false);

  function set(k, v) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!canWrite || !selectedSchoolYearId) {
      toast.error("Rok tylko do odczytu.");
      return;
    }
    setSaving(true);

    const { error } = await supabase.from("teacher_notices").insert([
      {
        ...form,
        school_year_id: selectedSchoolYearId,
        amount_per_child: form.amount_per_child
          ? parseFloat(form.amount_per_child)
          : null,
      },
    ]);

    setSaving(false);

    if (error) {
      toast.error("Błąd: " + error.message);
    } else {
      toast.success("Przypomnienie dodane!");
      setForm({
        title: "",
        description: "",
        amount_per_child: "",
        due_date: "",
        status: "Aktywne",
      });
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Tytuł *" className="sm:col-span-2">
          <input
            className={inputClass()}
            required
            value={form.title}
            onChange={(e) => set("title", e.target.value)}
          />
        </Field>

        <Field label="Opis" className="sm:col-span-2">
          <textarea
            className={textareaClass()}
            rows={3}
            value={form.description}
            onChange={(e) => set("description", e.target.value)}
          />
        </Field>

        <Field label="Kwota/dziecko (PLN)">
          <input
            className={inputClass()}
            type="number"
            step="0.01"
            value={form.amount_per_child}
            onChange={(e) => set("amount_per_child", e.target.value)}
          />
        </Field>

        <Field label="Termin">
          <input
            className={inputClass()}
            type="date"
            value={form.due_date}
            onChange={(e) => set("due_date", e.target.value)}
          />
        </Field>

        <Field label="Status">
          <select
            className={inputClass()}
            value={form.status}
            onChange={(e) => set("status", e.target.value)}
          >
            <option>Aktywne</option>
            <option value="zapłacone">Zapłacone</option>
            <option>Anulowane</option>
          </select>
        </Field>
      </div>

      {isSelectedYearDraft && <DraftYearMessage />}
      {!canWrite && <ReadOnlyYearMessage />}

      <button
        type="submit"
        className={primaryButtonClass(saving || !canWrite)}
        disabled={saving || !canWrite}
      >
        {saving ? "Dodawanie..." : "Dodaj przypomnienie"}
      </button>
    </form>
  );
}

function AddUserForm() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [saving, setSaving] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();

    if (password.length < 6) {
      toast.error("Hasło musi mieć co najmniej 6 znaków.");
      return;
    }
    if (password !== confirmPassword) {
      toast.error("Hasła nie są identyczne.");
      return;
    }

    setSaving(true);

    // Wywołanie Edge Function — service_role klucz pozostaje po stronie serwera
    const { data, error } = await supabase.functions.invoke("create-user", {
      body: { email: email.trim().toLowerCase(), password },
    });

    setSaving(false);

    if (error || data?.error) {
      const msg = (error?.message ?? data?.error ?? "").toLowerCase();
      if (msg.includes("już istnieje") || msg.includes("already")) {
        toast.error("Użytkownik z tym adresem e-mail już istnieje.");
      } else if (msg.includes("forbidden") || msg.includes("admin")) {
        toast.error("Brak uprawnień. Tylko administrator może tworzyć konta.");
      } else if (msg.includes("edge function")) {
        toast.error(
          "Edge Function nie jest wdrożona. Uruchom: supabase functions deploy create-user"
        );
      } else {
        toast.error(error?.message ?? data?.error ?? "Błąd tworzenia konta.");
      }
    } else {
      toast.success("Konto zostało utworzone. Użytkownik może się teraz zalogować.");
      setEmail("");
      setPassword("");
      setConfirmPassword("");
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5 max-w-md">
      <div className="rounded-2xl border border-emerald-100/80 bg-emerald-50/60 px-4 py-3">
        <p className="text-sm leading-6 text-slate-600">
          Utwórz konto dla rodzica lub innego użytkownika. Po utworzeniu konta
          użytkownik może logować się hasłem lub linkiem e-mail.
        </p>
      </div>

      <div className="rounded-2xl border border-sky-100 bg-sky-50/70 px-4 py-3">
        <p className="text-xs leading-5 text-sky-700">
          Wymaga wdrożenia Edge Function{" "}
          <code className="rounded bg-sky-100 px-1">create-user</code>.
          Instrukcja poniżej w sekcji „Konfiguracja".
        </p>
      </div>

      <Field label="Adres e-mail *">
        <input
          className={inputClass()}
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="jan.kowalski@gmail.com"
          autoComplete="off"
        />
      </Field>

      <Field label="Hasło początkowe *">
        <input
          className={inputClass()}
          type="password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="min. 6 znaków"
          autoComplete="new-password"
        />
      </Field>

      <Field label="Potwierdź hasło *">
        <input
          className={inputClass()}
          type="password"
          required
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          placeholder="powtórz hasło"
          autoComplete="new-password"
        />
      </Field>

      <button type="submit" className={primaryButtonClass(saving)} disabled={saving}>
        {saving ? (
          <span className="flex items-center gap-2">
            <span className="h-4 w-4 rounded-full border-2 border-white border-t-transparent animate-spin" />
            Tworzenie konta...
          </span>
        ) : (
          "Utwórz konto"
        )}
      </button>

      {/* Instrukcja wdrożenia */}
      <details className="rounded-2xl border border-slate-100 bg-slate-50/70">
        <summary className="cursor-pointer px-4 py-3 text-xs font-semibold text-slate-500 select-none">
          Konfiguracja Edge Function — rozwiń instrukcję
        </summary>
        <div className="border-t border-slate-100 px-4 py-4 space-y-2 text-xs leading-6 text-slate-500">
          <p>
            <strong className="text-slate-700">1.</strong> Zainstaluj Supabase CLI:{" "}
            <code className="rounded bg-slate-100 px-1">npm install -g supabase</code>
          </p>
          <p>
            <strong className="text-slate-700">2.</strong> Zaloguj się:{" "}
            <code className="rounded bg-slate-100 px-1">supabase login</code>
          </p>
          <p>
            <strong className="text-slate-700">3.</strong> Połącz projekt:{" "}
            <code className="rounded bg-slate-100 px-1">supabase link --project-ref PROJECT_REF</code>
          </p>
          <p>
            <strong className="text-slate-700">4.</strong> Wdróż funkcję:{" "}
            <code className="rounded bg-slate-100 px-1">supabase functions deploy create-user</code>
          </p>
          <p className="mt-2 text-slate-400">
            Alternatywnie: twórz użytkowników przez Supabase Dashboard →
            Authentication → Users → Add user.
          </p>
        </div>
      </details>
    </form>
  );
}

export default function AdminScreen() {
  const [searchParams] = useSearchParams();

  const tabFromUrl = searchParams.get("tab");

  const initialTab =
    tabFromUrl === "collection"
      ? 1
      : tabFromUrl === "expense"
      ? 0
      : tabFromUrl === "planned"
      ? 2
      : tabFromUrl === "notice"
      ? 3
      : tabFromUrl === "user"
      ? 4
      : 0;

  const [activeTab, setActiveTab] = useState(initialTab);

  const forms = [
    <AddExpenseForm key="expense" />,
    <AddCollectionForm key="collection" />,
    <AddPlannedForm key="planned" />,
    <AddNoticeForm key="notice" />,
    <AddUserForm key="user" />,
  ];

  return (
    <div className="space-y-6 lg:space-y-8 max-w-5xl">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="rounded-[32px] border border-white/70 bg-[linear-gradient(135deg,rgba(255,255,255,0.82)_0%,rgba(236,253,245,0.72)_45%,rgba(255,247,237,0.76)_100%)] px-6 py-7 shadow-[0_18px_52px_rgba(15,23,42,0.06)]"
      >
        <div className="max-w-3xl">
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-emerald-700/80">
            Administrator
          </div>
          <h1 className="mt-3 text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">
            Panel admina
          </h1>
          <p className="mt-3 max-w-2xl text-base leading-7 text-slate-600">
            Dodawanie nowych wydatków, zbiórek, planowanych kosztów i przypomnień w jednym miejscu.
          </p>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.08, duration: 0.35 }}
        className="flex flex-wrap gap-2"
      >
        {TABS.map((tab, i) => (
          <button
            key={tab}
            onClick={() => setActiveTab(i)}
            className={`rounded-2xl px-4 py-2.5 text-sm font-semibold transition-all duration-200 ${
              activeTab === i
                ? "bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-[0_10px_24px_rgba(16,185,129,0.22)]"
                : "border border-white/80 bg-white/75 text-slate-600 shadow-[0_8px_20px_rgba(15,23,42,0.04)] hover:bg-emerald-50 hover:text-emerald-700"
            }`}
          >
            Dodaj: {tab}
          </button>
        ))}
      </motion.div>

      <motion.div
        key={activeTab}
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
        className="rounded-[30px] border border-white/70 bg-white/82 p-6 shadow-[0_18px_48px_rgba(15,23,42,0.06)] backdrop-blur-sm"
      >
        <div className="mb-6">
          <div className="text-xl font-semibold tracking-tight text-slate-900">
            Dodaj: {TABS[activeTab]}
          </div>
          <div className="mt-1 text-sm text-slate-500">
            Wypełnij formularz i zapisz dane w systemie.
          </div>
        </div>

        {forms[activeTab]}
      </motion.div>
    </div>
  );
}
